import React from "react";
import permissionsService from "@/services/permissionsService";
import { kpiCardHref } from "@/modules/hub/features/MemberDashboard/config/kpiConfig";
import { KpiRelation } from "../../hooks/useAccountMappingSection";

interface KpiRelationsLineProps {
  relations: KpiRelation[];
}

/**
 * What the dashboard does with this ledger row, as one labelled sentence:
 * `Needed for Office labor · counted in Net profit, Days cash on hand`.
 *
 * Labelled rather than a bare list of card names, which stated no relationship
 * — "Retained earnings" under a row called Retained Earnings & Net Income could
 * be a description, a category or a card. And not a badge: after the catalog
 * consolidations all but one row carries a relation, and a badge on eighteen of
 * nineteen rows is wallpaper.
 *
 * This line replaced the `Required` badge outright on 2026-09-18. The two were
 * driven by the same predicate, so the badge said "some KPI names this" directly
 * above a line naming which.
 *
 * A file of its own because the page and the picker drawer both render it, and
 * the link-or-text decision below should not be made twice.
 */
const KpiRelationsLine: React.FC<KpiRelationsLineProps> = ({ relations }) => {
  const { hasPermission } = permissionsService();

  /**
   * The mapping page sits behind `integrations` and the dashboard behind
   * `member-dashboard`, which are granted separately — so a member can be
   * entitled to map accounts and not to see the cards. They still need the card
   * named, they just cannot be sent to it: a link would land them on the
   * forbidden page.
   */
  const canOpenDashboard = hasPermission("member-dashboard");

  if (relations.length === 0) {
    return null;
  }

  return (
    /* One line each rather than two clauses of one sentence. Run together they
       read as a single run of grey text and the second half — the one saying
       this row changes a figure without being required — was the half that got
       skipped. `mt-2` separates the pair from the description above, which they
       were crowding. */
    <div className="mt-2 space-y-0.5">
      {relations.map((relation) => (
        <div
          className="text-xs text-gray-500 dark:text-gray-400"
          key={relation.label}
        >
          {`${relation.label}: `}
          {relation.kpis.map((kpi, kpiIndex) => (
            <span key={kpi.code}>
              {kpiIndex > 0 && ", "}
              {canOpenDashboard ? (
                /* A new tab, deliberately. Mapping a chart of accounts carries
                   state — scroll position, an open drawer, a part-worked review
                   queue — and this link means "go look at what this affects",
                   not "go do something else". */
                <a
                  className="text-gray-700 underline decoration-gray-300 underline-offset-2 hover:text-primary hover:decoration-primary dark:text-gray-200 dark:decoration-gray-600"
                  href={kpiCardHref(kpi.code)}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {kpi.name}
                </a>
              ) : (
                <span className="text-gray-700 dark:text-gray-200">
                  {kpi.name}
                </span>
              )}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
};

export default KpiRelationsLine;
